export type ProgressEntry = [number, number, number[]?];

export type FlatProgressState = Record<string, ProgressEntry[]>;

// Top-level key is a brandingId, or NO_BRANDING_PROGRESS_KEY when progress was
// recorded with no active branding (or migrated from the pre-branding flat shape).
export type ProgressEnvelope = Record<string, FlatProgressState>;

export const NO_BRANDING_PROGRESS_KEY = 'none';

// Pre-branding records store the flat { [trigger]: ProgressEntry[] } shape directly
// at the top level — every value is an array. Branding-scoped envelopes nest one
// level deeper, so every top-level value is itself an object, never an array.
const isLegacyFlatProgressState = (
  parsed: Record<string, unknown>,
): boolean =>
  Object.keys(parsed).length > 0 &&
  Object.values(parsed).every((value) => Array.isArray(value));

export const parseProgressEnvelope = (
  raw: string | null | undefined,
): ProgressEnvelope => {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(raw || '{}');
  } catch {
    return {};
  }

  if (isLegacyFlatProgressState(parsed)) {
    return { [NO_BRANDING_PROGRESS_KEY]: parsed as FlatProgressState };
  }

  return parsed as ProgressEnvelope;
};

export const getProgressBrandingKey = (
  brandingId: string | null | undefined,
): string => brandingId || NO_BRANDING_PROGRESS_KEY;

export const decodeProgressForBranding = (
  raw: string | null | undefined,
  brandingId: string | null | undefined,
): FlatProgressState =>
  parseProgressEnvelope(raw)[getProgressBrandingKey(brandingId)] ?? {};

export const encodeProgressForBranding = (
  existingEnvelope: ProgressEnvelope,
  brandingId: string | null | undefined,
  flatState: FlatProgressState,
): string => {
  const envelope: ProgressEnvelope = {
    ...existingEnvelope,
    [getProgressBrandingKey(brandingId)]: flatState,
  };
  return JSON.stringify(envelope);
};
